import React, { useState, useEffect, useCallback } from 'react';
import { AttributeRenderer } from '../modules/attribute/AttributeRenderer';
import { ProductForm } from '../modules/product/ProductForm';
import { AttributeService } from '../modules/attribute/AttributeService';
import { BulkImportTab } from './BulkImportTab';
import { API_BASE, getToken } from '../../../lib/api';
import '../styles/engine.css';

// ─── Field Types ──────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { group:'1. BASIC INPUT', options:[{v:'text',l:'Text'},{v:'textarea',l:'Textarea'},{v:'number',l:'Number'},{v:'decimal',l:'Decimal'},{v:'password',l:'Password'}] },
  { group:'2. SELECTION', options:[{v:'dropdown',l:'Dropdown'},{v:'multi-select',l:'Multi Select'},{v:'radio',l:'Radio Button'},{v:'checkbox',l:'Checkbox'}] },
  { group:'3. DATE & TIME', options:[{v:'date',l:'Date Picker'},{v:'time',l:'Time Picker'},{v:'datetime',l:'DateTime Picker'}] },
  { group:'4. FILE & MEDIA', options:[{v:'file-upload',l:'File Upload'},{v:'image-url',l:'Image Upload'}] },
  { group:'5. LOOKUP', options:[{v:'lookup',l:'Lookup (Searchable)'},{v:'autocomplete',l:'AutoComplete'},{v:'entity-picker',l:'Entity Picker'}] },
  { group:'6. COMPUTED', options:[{v:'calculated',l:'Calculated Field'},{v:'formula',l:'Formula Field'}] },
  { group:'7. RELATIONAL', options:[{v:'grid',l:'Table Grid (Line Items)'}] },
  { group:'8. ADDRESS', options:[{v:'address',l:'Address Component'}] },
  { group:'11. SPECIAL ERP', options:[{v:'barcode',l:'Barcode Scanner'},{v:'qr-code',l:'QR Code'},{v:'currency',l:'Currency Field'},{v:'tax-selector',l:'Tax Selector'},{v:'status',l:'Status Field'},{v:'workflow',l:'Approval Workflow'}] },
];

// ─── Type Badge Color Map ────────────────────────────────────────────────────
const TYPE_COLORS = {
  text:'#475569', textarea:'#475569', number:'#0369a1', decimal:'#0369a1', currency:'#0369a1',
  dropdown:'#7c3aed', 'multi-select':'#7c3aed', radio:'#7c3aed', checkbox:'#7c3aed',
  date:'#0891b2', time:'#0891b2', datetime:'#0891b2',
  'image-url':'#be185d', 'file-upload':'#be185d',
  lookup:'#059669', autocomplete:'#059669', 'entity-picker':'#059669',
  grid:'#d97706', address:'#d97706',
  status:'#dc2626', workflow:'#dc2626', barcode:'#6b7280', 'qr-code':'#6b7280',
  'tax-selector':'#ea580c', boolean:'#065f46', formula:'#1d4ed8', calculated:'#1d4ed8',
};

// ─── Spinner ─────────────────────────────────────────────────────────────────
function Spinner({ size = 18, color = '#2563eb' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation:'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2.5" strokeDasharray="40 20" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Skeleton Row ────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div style={{ padding:'0.6rem 0.75rem', borderRadius:'6px', marginBottom:'2px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px' }}>
        <div style={{ height:'13px', background:'#e2e8f0', borderRadius:'4px', width:'60%', animation:'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height:'13px', background:'#e2e8f0', borderRadius:'4px', width:'20%', animation:'pulse 1.5s ease-in-out infinite' }} />
      </div>
      <div style={{ height:'10px', background:'#f1f5f9', borderRadius:'4px', width:'40%', marginTop:'5px', animation:'pulse 1.5s ease-in-out infinite' }} />
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}

// ─── LayoutVisualizer ────────────────────────────────────────────────────────
function LayoutVisualizer({ placements }) {
  if (!placements?.length) return null;
  return (
    <div style={{ marginTop:'1.5rem', padding:'1rem', border:'1px solid #e2e8f0', borderRadius:'8px', background:'#f8fafc' }}>
      <h4 style={{ fontSize:'0.7rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.75rem' }}>Visual Layout Preview (12-col grid)</h4>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(12, 1fr)', gap:'5px', background:'#fff', padding:'8px', borderRadius:'6px', border:'1px solid #cbd5e1', minHeight:'60px' }}>
        {placements.map((p, idx) => (
          <div key={idx} style={{ gridRow:p.row, gridColumn:`${p.colStart} / span ${p.colSpan}`, background:'#2563eb', color:'#fff', padding:'6px 4px', fontSize:'0.6rem', fontWeight:600, borderRadius:'3px', textAlign:'center', minHeight:'32px', display:'flex', alignItems:'center', justifyContent:'center', wordBreak:'break-all' }}>
            {p.groupId}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FormField helper ────────────────────────────────────────────────────────
function FF({ label, children, full }) {
  return (
    <div style={full ? { gridColumn:'1 / -1' } : {}}>
      <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}
const inp = { width:'100%', padding:'0.42rem 0.65rem', border:'1px solid #e2e8f0', borderRadius:'6px', fontSize:'0.85rem', outline:'none', background:'#fafafa', color:'#0f172a', fontFamily:'inherit' };
const inp2 = { ...inp, background:'#f8fafc' };

// ─── AdminPanel ──────────────────────────────────────────────────────────────
const BLANK_SF = { ScreenName:'', FieldKey:'', FieldLabel:'', FieldType:'text', IsRequired:false, SectionName:'Additional Fields', DisplayOrder:0, PlaceholderText:'', DropdownOptions:'' };

export function AdminPanel() {
  const [activeTab, setActiveTab] = useState('attributes');
  const [attributes, setAttributes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [companyConfig, setCompanyConfig] = useState({ baseCurrency:'INR', defaultLanguage:'en', supportedLanguages:['en','hi'], supportedCurrencies:['INR','USD'], timezone:'Asia/Kolkata', dateFormat:'DD-MM-YYYY' });

  const [selectedAttr, setSelectedAttr] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [selectedPreviewSolution, setSelectedPreviewSolution] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewValue, setPreviewValue] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(true);

  // ─── Screen Fields (DynamicFieldsConfig) ─────────────────────────────────
  const [screenFields, setScreenFields]         = useState([]);
  const [sfLoading, setSfLoading]               = useState(false);
  const [sfSelectedScreen, setSfSelectedScreen] = useState(null);
  const [sfSearchTerm, setSfSearchTerm]         = useState('');
  const [sfShowForm, setSfShowForm]             = useState(false);
  const [sfSaving, setSfSaving]                 = useState(false);
  const [sfField, setSfField]                   = useState(BLANK_SF);

  // ─── useCallback for screen fields reload (used by handlers, NOT by useEffect) ──
  const loadScreenFields = useCallback(async () => {
    setSfLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/dynamic-fields/config/all`, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
      const json = await res.json();
      if (json.success) setScreenFields(json.data || []);
    } catch (e) { console.error('[AdminPanel] Screen fields load error:', e); }
    setSfLoading(false);
  }, []);

  // ─── Event handlers (MUST be before useEffect blocks) ────────────────────
  const handleSaveAttribute = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedAttr) return;
    setIsSaving(true);
    try {
      await AttributeService.saveAttribute(selectedAttr);
      const data = await AttributeService.getAttributes(selectedIndustry || undefined);
      setAttributes((data || []).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i));
      alert('Attribute saved successfully!');
    } catch (err) { alert(`Error: ${err.message}`); }
    finally { setIsSaving(false); }
  }, [selectedAttr, selectedIndustry]);

  const handleSaveLayout = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedLayout) return;
    setIsSaving(true);
    try {
      await AttributeService.saveLayout(selectedLayout);
      const data = await AttributeService.getLayouts();
      setLayouts((data || []).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i));
      alert('Layout saved!');
    } catch (err) { alert(`Error: ${err.message}`); }
    finally { setIsSaving(false); }
  }, [selectedLayout]);

  const handleSaveSolution = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedSolution) return;
    setIsSaving(true);
    try {
      await AttributeService.saveSolution(selectedSolution);
      const data = await AttributeService.getSolutions();
      setSolutions(data || []);
      alert('Solution saved!');
    } catch (err) { alert(`Error: ${err.message}`); }
    finally { setIsSaving(false); }
  }, [selectedSolution]);

  const handleSaveScreenField = useCallback(async (e) => {
    e.preventDefault();
    if (!sfField.ScreenName || !sfField.FieldKey || !sfField.FieldLabel) return;
    setSfSaving(true);
    const screenName = sfField.ScreenName;
    try {
      const res  = await fetch(`${API_BASE}/api/dynamic-fields/config`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(sfField),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Save failed');
      await loadScreenFields();
      setSfShowForm(false);
      setSfField(BLANK_SF);
      setSfSelectedScreen(screenName);
    } catch (err) { alert(`Error saving field: ${err.message}`); }
    finally { setSfSaving(false); }
  }, [sfField, loadScreenFields]);

  const handleDeleteScreenField = useCallback(async (id) => {
    if (!window.confirm('Delete this field? This cannot be undone.')) return;
    try {
      const res  = await fetch(`${API_BASE}/api/dynamic-fields/config/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Delete failed');
      await loadScreenFields();
    } catch (err) { alert(`Error deleting field: ${err.message}`); }
  }, [loadScreenFields]);

  // ─── Tab / industry helpers (MUST be before useEffect blocks) ────────────
  const updateTab = (tab) => { setActiveTab(tab); setSelectedIndustry(null); setSearchTerm(''); setSelectedAttr(null); setSelectedLayout(null); setSelectedSolution(null); };
  const selectIndustry = (id) => { setSelectedIndustry(id); setSelectedAttr(null); setSelectedLayout(null); setSelectedSolution(null); setSearchTerm(''); };

  // ─── useEffect blocks (all use inline IIFEs — no external setState callers) ──
  // Initial full load
  useEffect(() => {
    const load = async () => {
      setIsLoadingAll(true);
      setIsLoadingIndustries(true);
      try {
        const [inds, lays, sols, cfg] = await Promise.all([
          AttributeService.getIndustries(),
          AttributeService.getLayouts(),
          AttributeService.getSolutions(),
          AttributeService.getCompanyConfig(),
        ]);
        setIndustries(inds || []);
        setLayouts((lays || []).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i));
        setSolutions(sols || []);
        setCompanyConfig(cfg || { baseCurrency:'INR', defaultLanguage:'en', supportedLanguages:['en','hi'], supportedCurrencies:['INR','USD'], timezone:'Asia/Kolkata', dateFormat:'DD-MM-YYYY' });
      } catch (e) { console.error('[AdminPanel] Load error:', e); }
      finally { setIsLoadingIndustries(false); setIsLoadingAll(false); }
    };
    load();
  }, []);

  // Reload attributes when industry changes
  useEffect(() => {
    const load = async () => {
      setIsLoadingItems(true);
      try {
        const attrs = await AttributeService.getAttributes(selectedIndustry || undefined);
        setAttributes((attrs || []).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i));
      } catch (e) { console.error('[AdminPanel] Attrs load error:', e); }
      finally { setIsLoadingItems(false); }
    };
    load();
  }, [selectedIndustry]);

  // Compute initial preview value for the attribute editor
  useEffect(() => {
    const compute = () => {
      if (!selectedAttr) return;
      let init = selectedAttr.defaultValue ?? null;
      if (init === null) {
        if (selectedAttr.type === 'grid') init = [];
        else if (selectedAttr.type === 'address') init = {};
        else if (selectedAttr.type === 'multi-select') init = [];
      }
      setPreviewValue(init);
    };
    compute();
  }, [selectedAttr?.id]);

  // Load screen fields when Screen Fields tab is active
  useEffect(() => {
    if (activeTab !== 'screen_fields') return;
    const load = async () => {
      setSfLoading(true);
      try {
        const res  = await fetch(`${API_BASE}/api/dynamic-fields/config/all`, { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } });
        const json = await res.json();
        if (json.success) setScreenFields(json.data || []);
      } catch (e) { console.error('[AdminPanel] Screen fields load error:', e); }
      setSfLoading(false);
    };
    load();
  }, [activeTab]);

  // Build industry categories
  const indCategories = Array.from(new Set(industries.map(i => i.category))).filter(Boolean);
  const allInds = [{ id:'Common', name:'Common', icon:'🌐', category:'Global' }, ...industries];
  const allCats = ['Global', ...indCategories];

  // Filtered list items in panel
  const st = searchTerm.toLowerCase();
  const filteredItems = (() => {
    if (!selectedIndustry) return [];
    if (activeTab === 'attributes')
      return attributes.filter(a => (a.industry === selectedIndustry || a.industry === 'Common') && (!st || a.label?.toLowerCase().includes(st) || a.id?.toLowerCase().includes(st)));
    if (activeTab === 'layouts')
      return layouts.filter(l => (l.industry === selectedIndustry || l.id?.includes(selectedIndustry.toLowerCase())) && (!st || l.label?.toLowerCase().includes(st) || l.id?.toLowerCase().includes(st)));
    if (activeTab === 'solutions')
      return solutions.filter(s => (s.industry === selectedIndustry || s.id?.includes(selectedIndustry.toLowerCase())) && (!st || s.label?.toLowerCase().includes(st) || s.id?.toLowerCase().includes(st)));
    return [];
  })();

  const isItemSelected = (item) => {
    if (activeTab === 'attributes') return selectedAttr?.id === item.id;
    if (activeTab === 'layouts') return selectedLayout?.id === item.id;
    if (activeTab === 'solutions') return selectedSolution?.id === item.id;
    return false;
  };

  const selectItem = (item) => {
    if (activeTab === 'attributes') setSelectedAttr(item);
    if (activeTab === 'layouts') setSelectedLayout(item);
    if (activeTab === 'solutions') setSelectedSolution(item);
  };

  const createNew = () => {
    if (activeTab === 'attributes') setSelectedAttr({ id:`attr_${Date.now()}`, label:'New Attribute', type:'text', industry: selectedIndustry || 'Common', scope:'form' });
    if (activeTab === 'layouts') setSelectedLayout({ id:`layout_${Date.now()}`, label:'New Layout', industry: selectedIndustry || 'Common', placements:[] });
    if (activeTab === 'solutions') setSelectedSolution({ id:`sol_${Date.now()}`, label:'New Solution', industry: selectedIndustry || 'Common', description:'', icon:'📦', layoutId:'', overrides:{}, conditionalRules:[] });
  };

  const tabs = [
    { id:'attributes',   label:'Attributes',    icon:'📝' },
    { id:'layouts',      label:'Layouts',        icon:'📐' },
    { id:'solutions',    label:'Solutions',      icon:'💡' },
    { id:'screen_fields',label:'Screen Fields',  icon:'🔧' },
    { id:'company',      label:'Company',        icon:'🏢' },
    { id:'bulk_import',  label:'Bulk Import',    icon:'📥' },
    { id:'preview',      label:'Preview',        icon:'👁' },
  ];

  // ── UI Styles ────────────────────────────────────────────────────────────
  const panelBg = '#ffffff';
  const borderColor = '#e2e8f0';
  const textMuted = '#64748b';
  const textPrimary = '#0f172a';
  const accent = '#2563eb';
  const bgPage = '#f1f5f9';

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background: bgPage, fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", fontSize:'14px', color: textPrimary }}>

      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.6rem 1.25rem', background:'white', borderBottom:`1px solid ${borderColor}`, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
          <span style={{ width:28, height:28, background:accent, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:'0.9rem', flexShrink:0 }}>⚙</span>
          <div>
            <div style={{ fontWeight:800, fontSize:'0.9rem', color:textPrimary, lineHeight:1.2 }}>ERP Attribute Engine</div>
            <div style={{ fontSize:'0.68rem', color:textMuted }}>Configure attributes, layouts &amp; solutions</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:'2px', background:'#f1f5f9', padding:'3px', borderRadius:'10px', border:`1px solid ${borderColor}` }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => updateTab(tab.id)}
              style={{ padding:'0.38rem 0.9rem', borderRadius:'7px', border:'none', cursor:'pointer', fontSize:'0.72rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.3rem', whiteSpace:'nowrap', background: activeTab === tab.id ? accent : 'transparent', color: activeTab === tab.id ? 'white' : textMuted, transition:'all 0.15s' }}>
              <span style={{ fontSize:'0.8rem' }}>{tab.icon}</span>{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', overflow:'hidden', minHeight:0 }}>

        {/* ── LEFT PANEL ─────────────────────────────────────────────────────── */}
        {!['preview','company','bulk_import','screen_fields'].includes(activeTab) && (
          <div style={{ width:268, flexShrink:0, borderRight:`1px solid ${borderColor}`, background:panelBg, display:'flex', flexDirection:'column', overflow:'hidden' }}>

            {/* Panel header */}
            <div style={{ padding:'0.6rem 0.75rem', borderBottom:`1px solid #f1f5f9`, flexShrink:0 }}>
              {selectedIndustry ? (
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem' }}>
                  <button onClick={() => { setSelectedIndustry(null); setSelectedAttr(null); setSelectedLayout(null); setSelectedSolution(null); setSearchTerm(''); }}
                    style={{ padding:'0.2rem 0.5rem', border:`1px solid ${borderColor}`, borderRadius:'5px', background:'#f8fafc', cursor:'pointer', fontSize:'0.75rem', color:textMuted, display:'flex', alignItems:'center', gap:'3px' }}>
                    ← Back
                  </button>
                  <span style={{ fontSize:'0.8rem', fontWeight:700, color:textPrimary, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {allInds.find(i => i.id === selectedIndustry)?.icon} {allInds.find(i => i.id === selectedIndustry)?.name || selectedIndustry}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize:'0.65rem', fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.5rem' }}>
                  {isLoadingIndustries ? (
                    <span style={{ display:'flex', alignItems:'center', gap:'6px' }}><Spinner size={12} color="#94a3b8" /> Loading industries...</span>
                  ) : (
                    <span>{industries.length} Industries</span>
                  )}
                </div>
              )}

              {/* Create New button — ABOVE search */}
              {!['preview','company','bulk_import','screen_fields'].includes(activeTab) && (
                <button onClick={createNew}
                  style={{ width:'100%', padding:'0.38rem 0.65rem', border:`1px dashed ${accent}`, borderRadius:'6px', background:'#eff6ff', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, color:accent, display:'flex', alignItems:'center', justifyContent:'center', gap:'5px', marginBottom:'0.45rem' }}>
                  + Create New {activeTab.slice(0, -1)}
                </button>
              )}

              {/* Search */}
              <div style={{ position:'relative' }}>
                <input style={{ ...inp, paddingLeft:'2rem', fontSize:'0.78rem', background:'#f8fafc' }}
                  placeholder={`Search ${activeTab}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                <span style={{ position:'absolute', left:'0.6rem', top:'50%', transform:'translateY(-50%)', color:'#94a3b8', fontSize:'0.75rem' }}>🔍</span>
              </div>
            </div>

            {/* List */}
            <div style={{ flex:1, overflowY:'auto', padding:'0.4rem 0' }}>
              {!selectedIndustry ? (
                // Industry grid
                isLoadingIndustries ? (
                  <div style={{ padding:'1rem 0.75rem' }}>
                    {[...Array(8)].map((_, i) => (
                      <div key={i} style={{ height:'36px', background:'#f1f5f9', borderRadius:'6px', marginBottom:'5px', animation:'pulse 1.5s ease-in-out infinite' }} />
                    ))}
                  </div>
                ) : (
                  allCats.map(cat => {
                    const catInds = allInds.filter(i => i.category === cat);
                    if (!catInds.length) return null;
                    return (
                      <div key={cat}>
                        <div style={{ padding:'0.25rem 0.75rem', fontSize:'0.6rem', fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', background:'#fafafa', borderBottom:'1px solid #f1f5f9', borderTop:'1px solid #f1f5f9' }}>{cat}</div>
                        {catInds.map(ind => (
                          <button key={ind.id} onClick={() => selectIndustry(ind.id)}
                            style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.45rem 0.75rem', border:'none', borderBottom:'1px solid #f8fafc', cursor:'pointer', textAlign:'left', background:'transparent', transition:'background 0.1s' }}
                            onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = '#f0f9ff'; }}
                            onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                            <span style={{ fontSize:'1rem', flexShrink:0 }}>{ind.icon}</span>
                            <span style={{ fontSize:'0.78rem', fontWeight:600, color:textPrimary, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ind.name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })
                )
              ) : (
                // Attribute/Layout/Solution list
                isLoadingItems ? (
                  <div style={{ padding:'0.25rem' }}>
                    {[...Array(10)].map((_, i) => <SkeletonRow key={i} />)}
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div style={{ padding:'2rem 1rem', textAlign:'center', color:textMuted, fontSize:'0.78rem' }}>
                    No {activeTab} found.<br/>
                    <span style={{ fontSize:'0.7rem', color:'#94a3b8' }}>Try a different search or industry.</span>
                  </div>
                ) : (
                  filteredItems.map(item => {
                    const selected = isItemSelected(item);
                    const typeColor = TYPE_COLORS[item.type] || '#475569';
                    return (
                      <button key={item.id} onClick={() => selectItem(item)}
                        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.5rem 0.75rem', border:'none', borderBottom:'1px solid #f8fafc', cursor:'pointer', textAlign:'left', background: selected ? '#eff6ff' : 'transparent', borderLeft: selected ? `3px solid ${accent}` : '3px solid transparent', transition:'all 0.1s' }}
                        onMouseEnter={e => { if (!e.currentTarget) return; if (!selected) e.currentTarget.style.background='#f8fafc'; }}
                        onMouseLeave={e => { if (!e.currentTarget) return; if (!selected) e.currentTarget.style.background='transparent'; }}>
                        <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                          <div style={{ fontSize:'0.8rem', fontWeight:700, color: selected ? accent : textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {item.label || item.id}
                          </div>
                          <div style={{ fontSize:'0.65rem', color:'#94a3b8', fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:'1px' }}>{item.id}</div>
                        </div>
                        {item.type && (
                          <span style={{ fontSize:'0.58rem', padding:'2px 6px', borderRadius:'3px', background: selected ? accent : typeColor, color:'white', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.04em', flexShrink:0, marginLeft:'6px' }}>
                            {item.type}
                          </span>
                        )}
                      </button>
                    );
                  })
                )
              )}
            </div>
          </div>
        )}

        {/* ── MAIN WORKSPACE ──────────────────────────────────────────────────── */}
        <div style={{ flex:1, overflowY:'auto', minWidth:0, background: bgPage }}>
          <div style={{ maxWidth:'960px', margin:'0 auto', padding:'1.25rem 1.5rem' }}>

            {/* ── ATTRIBUTES EDITOR ─────────────────────────────────────────── */}
            {activeTab === 'attributes' && selectedAttr && (
              <form onSubmit={handleSaveAttribute}>
                {/* Editor header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
                  <div>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>
                      Attribute Editor
                    </div>
                    <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>
                      {selectedAttr.label}
                    </h2>
                    <div style={{ fontSize:'0.7rem', fontFamily:'monospace', color:'#94a3b8', marginTop:'0.15rem' }}>{selectedAttr.id}</div>
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem', flexShrink:0 }}>
                    <button type="button" onClick={() => setSelectedAttr(null)} style={{ padding:'0.38rem 0.85rem', border:`1px solid ${borderColor}`, borderRadius:'6px', background:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:textMuted }}>Cancel</button>
                    <button type="submit" disabled={isSaving} style={{ padding:'0.38rem 1rem', background: accent, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'6px' }}>
                      {isSaving ? <><Spinner size={14} color="white" /> Saving...</> : '✓ Save Attribute'}
                    </button>
                  </div>
                </div>

                {/* Core fields */}
                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
                  <div style={{ fontSize:'0.68rem', fontWeight:800, color:textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.85rem', display:'flex', alignItems:'center', gap:'6px' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background: accent, display:'inline-block' }} />
                    Core Settings
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.85rem' }}>
                    <FF label="Attribute ID"><input style={inp} value={selectedAttr.id||''} onChange={e => setSelectedAttr({...selectedAttr, id:e.target.value})} /></FF>
                    <FF label="Label"><input style={inp} value={selectedAttr.label||''} onChange={e => setSelectedAttr({...selectedAttr, label:e.target.value})} /></FF>
                    <FF label="Field Type">
                      <select style={inp} value={selectedAttr.type} onChange={e => setSelectedAttr({...selectedAttr, type:e.target.value})}>
                        {FIELD_TYPES.map(g => (
                          <optgroup key={g.group} label={g.group}>
                            {g.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </FF>
                    <FF label="Industry">
                      <select style={inp} value={selectedAttr.industry||'Common'} onChange={e => setSelectedAttr({...selectedAttr, industry:e.target.value})}>
                        <option value="Common">Common (All Industries)</option>
                        {industries.sort((a,b) => a.name.localeCompare(b.name)).map(ind => <option key={ind.id} value={ind.id}>{ind.name}</option>)}
                      </select>
                    </FF>
                    <FF label="Scope">
                      <select style={inp} value={selectedAttr.scope||'form'} onChange={e => setSelectedAttr({...selectedAttr, scope:e.target.value})}>
                        <option value="form">Form Based</option>
                        <option value="control">Control Based</option>
                        <option value="solution">Solution Based</option>
                        <option value="global">Global</option>
                      </select>
                    </FF>
                    <FF label="Placeholder"><input style={inp} value={selectedAttr.placeholder||''} onChange={e => setSelectedAttr({...selectedAttr, placeholder:e.target.value})} /></FF>
                    <FF label="Help Text" full><input style={inp} value={selectedAttr.helpText||''} onChange={e => setSelectedAttr({...selectedAttr, helpText:e.target.value})} /></FF>
                  </div>
                </div>

                {/* Validation */}
                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
                  <div style={{ fontSize:'0.68rem', fontWeight:800, color:textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.85rem', display:'flex', alignItems:'center', gap:'6px' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'#f59e0b', display:'inline-block' }} />
                    Validation Rules
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
                    {[{lbl:'Min Value',key:'min',type:'number'},{lbl:'Max Value',key:'max',type:'number'},{lbl:'Min Length',key:'minLength',type:'number'},{lbl:'Max Length',key:'maxLength',type:'number'},{lbl:'Pattern (Regex)',key:'pattern',type:'text'},{lbl:'Pattern Message',key:'patternMessage',type:'text'}].map(f => (
                      <FF key={f.key} label={f.lbl}>
                        <input type={f.type} style={inp} value={selectedAttr.validation?.[f.key]||''} onChange={e => setSelectedAttr({...selectedAttr, validation:{...(selectedAttr.validation||{}), [f.key]: f.type==='number' ? (e.target.value ? Number(e.target.value) : undefined) : e.target.value}})} />
                      </FF>
                    ))}
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', paddingTop:'1.25rem' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem', fontWeight:600, color:textPrimary, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!selectedAttr.validation?.required} onChange={e => setSelectedAttr({...selectedAttr, validation:{...(selectedAttr.validation||{}), required:e.target.checked}})} /> Required
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem', fontWeight:600, color:textPrimary, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!selectedAttr.readOnly} onChange={e => setSelectedAttr({...selectedAttr, readOnly:e.target.checked})} /> Read Only
                      </label>
                    </div>
                  </div>
                </div>

                {/* Globalization & DB Mapping */}
                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
                  <div style={{ fontSize:'0.68rem', fontWeight:800, color:'#0891b2', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.85rem', display:'flex', alignItems:'center', gap:'6px' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'#0891b2', display:'inline-block' }} />
                    Globalization &amp; DB Mapping
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
                    <FF label="Base Currency"><input style={inp2} value={selectedAttr.baseCurrency||''} onChange={e => setSelectedAttr({...selectedAttr, baseCurrency:e.target.value})} placeholder="e.g. INR" /></FF>
                    <FF label="Default Language"><input style={inp2} value={selectedAttr.language||''} onChange={e => setSelectedAttr({...selectedAttr, language:e.target.value})} placeholder="e.g. en" /></FF>
                    <FF label="Decimals"><input type="number" style={inp2} value={selectedAttr.decimalPlaces??''} onChange={e => setSelectedAttr({...selectedAttr, decimalPlaces: e.target.value==='' ? undefined : Number(e.target.value)})} /></FF>
                    <FF label="DB Table Name"><input style={inp2} value={selectedAttr.dbTableName||''} onChange={e => setSelectedAttr({...selectedAttr, dbTableName:e.target.value})} /></FF>
                    <FF label="DB Column Name"><input style={inp2} value={selectedAttr.dbColumnName||''} onChange={e => setSelectedAttr({...selectedAttr, dbColumnName:e.target.value})} /></FF>
                    <div style={{ display:'flex', alignItems:'center', gap:'1rem', paddingTop:'1.25rem' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem', fontWeight:600, color:textPrimary, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!selectedAttr.isMultiLanguage} onChange={e => setSelectedAttr({...selectedAttr, isMultiLanguage:e.target.checked})} /> Multi-Language
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem', fontWeight:600, color:textPrimary, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!selectedAttr.isPrimaryKey} onChange={e => setSelectedAttr({...selectedAttr, isPrimaryKey:e.target.checked})} /> Primary Key
                      </label>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div style={{ background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}`, padding:'1rem 1.25rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.85rem' }}>
                    <div style={{ fontSize:'0.68rem', fontWeight:800, color:textMuted, textTransform:'uppercase', letterSpacing:'0.05em' }}>Live Preview</div>
                    <span style={{ padding:'0.15rem 0.5rem', background:'#dcfce7', color:'#15803d', borderRadius:'999px', fontSize:'0.65rem', fontWeight:700 }}>Live</span>
                  </div>
                  <div style={{ background:'#f8fafc', padding:'1rem', borderRadius:'8px', border:`1px solid ${borderColor}` }}>
                    <AttributeRenderer attribute={selectedAttr} value={previewValue} isRequired={!!selectedAttr.validation?.required} isEditable={true} onChange={setPreviewValue} />
                  </div>
                  <div style={{ marginTop:'0.6rem', background:'#1e293b', borderRadius:'6px', padding:'0.6rem 0.85rem' }}>
                    <div style={{ fontSize:'0.6rem', color:'#64748b', fontFamily:'monospace', textTransform:'uppercase', marginBottom:'0.2rem' }}>Raw Value</div>
                    <code style={{ fontSize:'0.72rem', color:'#34d399', fontFamily:'monospace', wordBreak:'break-all' }}>{JSON.stringify(previewValue) ?? 'null'}</code>
                  </div>
                </div>
              </form>
            )}

            {/* ── LAYOUTS EDITOR ─────────────────────────────────────────────── */}
            {activeTab === 'layouts' && selectedLayout && (
              <form onSubmit={handleSaveLayout}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
                  <div>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Layout Editor</div>
                    <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>{selectedLayout.label}</h2>
                    <div style={{ fontSize:'0.7rem', fontFamily:'monospace', color:'#94a3b8', marginTop:'0.15rem' }}>{selectedLayout.id}</div>
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem' }}>
                    <button type="button" onClick={() => setSelectedLayout(null)} style={{ padding:'0.38rem 0.85rem', border:`1px solid ${borderColor}`, borderRadius:'6px', background:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:textMuted }}>Cancel</button>
                    <button type="submit" disabled={isSaving} style={{ padding:'0.38rem 1rem', background: accent, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'6px' }}>
                      {isSaving ? <><Spinner size={14} color="white" /> Saving...</> : '✓ Save Layout'}
                    </button>
                  </div>
                </div>

                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.85rem', marginBottom:'1.25rem' }}>
                    <FF label="Layout ID"><input style={inp} value={selectedLayout.id} onChange={e => setSelectedLayout({...selectedLayout, id:e.target.value})} /></FF>
                    <FF label="Layout Label"><input style={inp} value={selectedLayout.label} onChange={e => setSelectedLayout({...selectedLayout, label:e.target.value})} /></FF>
                  </div>

                  <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.65rem' }}>Section Placements (12-Column Grid)</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.45rem', marginBottom:'0.75rem' }}>
                    {(selectedLayout.placements||[]).map((p, idx) => (
                      <div key={idx} style={{ display:'grid', gridTemplateColumns:'2fr 60px 60px 60px 32px', gap:'0.45rem', alignItems:'end', background:'#f8fafc', padding:'0.65rem 0.75rem', borderRadius:'7px', border:`1px solid ${borderColor}` }}>
                        {[{lbl:'Group ID',key:'groupId',type:'text'},{lbl:'Row',key:'row',type:'number'},{lbl:'Col Start',key:'colStart',type:'number'},{lbl:'Col Span',key:'colSpan',type:'number'}].map(f => (
                          <div key={f.key}>
                            <label style={{ display:'block', fontSize:'0.6rem', fontWeight:600, color:'#94a3b8', marginBottom:'0.2rem' }}>{f.lbl}</label>
                            <input type={f.type} style={{ ...inp, padding:'0.3rem 0.4rem', fontSize:'0.78rem', background:'white' }} value={p[f.key]} onChange={e => {
                              const next = [...selectedLayout.placements]; next[idx] = {...p, [f.key]: f.type==='number' ? Number(e.target.value) : e.target.value}; setSelectedLayout({...selectedLayout, placements:next});
                            }} />
                          </div>
                        ))}
                        <button type="button" onClick={() => { const next=[...selectedLayout.placements]; next.splice(idx,1); setSelectedLayout({...selectedLayout, placements:next}); }}
                          style={{ padding:'0.3rem', background:'none', border:`1px solid #fca5a5`, borderRadius:'5px', color:'#ef4444', cursor:'pointer', fontSize:'0.7rem', alignSelf:'end' }}>✕</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setSelectedLayout({...selectedLayout, placements:[...(selectedLayout.placements||[]), {groupId:'grp_new', row:1, colStart:1, colSpan:12}]})}
                    style={{ width:'100%', padding:'0.55rem', border:`2px dashed ${borderColor}`, borderRadius:'7px', background:'transparent', color:textMuted, cursor:'pointer', fontSize:'0.78rem', fontWeight:600 }}>
                    + Add Section Placement
                  </button>
                </div>
                <LayoutVisualizer placements={selectedLayout.placements} />
              </form>
            )}

            {/* ── SOLUTIONS EDITOR ──────────────────────────────────────────── */}
            {activeTab === 'solutions' && selectedSolution && (
              <form onSubmit={handleSaveSolution}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
                  <div>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Solution Editor</div>
                    <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>{selectedSolution.label}</h2>
                  </div>
                  <div style={{ display:'flex', gap:'0.5rem' }}>
                    <button type="button" onClick={() => setSelectedSolution(null)} style={{ padding:'0.38rem 0.85rem', border:`1px solid ${borderColor}`, borderRadius:'6px', background:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:600, color:textMuted }}>Cancel</button>
                    <button type="submit" disabled={isSaving} style={{ padding:'0.38rem 1rem', background:accent, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'6px' }}>
                      {isSaving ? <><Spinner size={14} color="white" /> Saving...</> : '✓ Save Solution'}
                    </button>
                  </div>
                </div>
                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.85rem' }}>
                    {[{lbl:'Solution ID',key:'id'},{lbl:'Label',key:'label'},{lbl:'Icon',key:'icon'},{lbl:'Layout ID',key:'layoutId'}].map(f => (
                      <FF key={f.key} label={f.lbl}><input style={inp} value={selectedSolution[f.key]||''} onChange={e => setSelectedSolution({...selectedSolution, [f.key]:e.target.value})} /></FF>
                    ))}
                    <FF label="Description" full>
                      <input style={inp} value={selectedSolution.description||''} onChange={e => setSelectedSolution({...selectedSolution, description:e.target.value})} />
                    </FF>
                  </div>
                </div>
              </form>
            )}

            {/* ── COMPANY TAB ───────────────────────────────────────────────── */}
            {activeTab === 'company' && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
                  <div>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Company Configuration</div>
                    <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>Globalization &amp; Settings</h2>
                  </div>
                  <button onClick={async () => { setIsSaving(true); try { await AttributeService.saveCompanyConfig(companyConfig); alert('Saved!'); } catch(e) { alert(e.message); } finally { setIsSaving(false); } }}
                    style={{ padding:'0.38rem 1rem', background:accent, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'6px' }}>
                    {isSaving ? <><Spinner size={14} color="white" /> Saving...</> : '✓ Save Config'}
                  </button>
                </div>
                <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.85rem' }}>
                    {[{lbl:'Base Currency',key:'baseCurrency'},{lbl:'Default Language',key:'defaultLanguage'},{lbl:'Timezone',key:'timezone'},{lbl:'Date Format',key:'dateFormat'}].map(f => (
                      <FF key={f.key} label={f.lbl}><input style={inp} value={companyConfig[f.key]||''} onChange={e => setCompanyConfig({...companyConfig, [f.key]:e.target.value})} /></FF>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── PREVIEW TAB ───────────────────────────────────────────────── */}
            {activeTab === 'preview' && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}`, flexWrap:'wrap', gap:'0.75rem' }}>
                  <div>
                    <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Runtime Form Preview</div>
                    <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>Select a solution to preview the generated form</h2>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
                    {isLoadingAll && <Spinner size={16} />}
                    <select style={{ padding:'0.42rem 0.85rem', border:`1px solid ${borderColor}`, borderRadius:'7px', fontSize:'0.82rem', fontWeight:600, outline:'none', background:'white', minWidth:'220px' }}
                      value={selectedPreviewSolution} onChange={e => setSelectedPreviewSolution(e.target.value)}>
                      <option value="">-- Select Industry Solution --</option>
                      {solutions.sort((a, b) => a.label.localeCompare(b.label)).map(s => (
                        <option key={s.id} value={s.id}>{s.icon} {s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedPreviewSolution ? (
                  <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem' }}>
                    <ProductForm
                      solutionId={selectedPreviewSolution}
                      onSubmit={async (v) => { console.log('Form Submitted:', v); alert(`Form submitted! ${Object.keys(v).length} fields captured. Check console for details.`); }}
                    />
                  </div>
                ) : (
                  <div style={{ padding:'5rem', textAlign:'center', background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}` }}>
                    <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>👁</div>
                    <h3 style={{ fontSize:'1rem', fontWeight:700, color:textPrimary, marginBottom:'0.5rem' }}>No Solution Selected</h3>
                    <p style={{ color:textMuted, fontSize:'0.85rem' }}>Choose an industry solution from the dropdown above to see the live form.</p>
                    {solutions.length === 0 && !isLoadingAll && (
                      <p style={{ color:'#ef4444', fontSize:'0.78rem', marginTop:'0.5rem' }}>No solutions found. Make sure the DB is connected and seeded.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── BULK IMPORT TAB ───────────────────────────────────────────── */}
            {activeTab === 'bulk_import' && (
              <BulkImportTab onImportComplete={async () => {
                // Reload attributes after bulk import
                setIsLoadingItems(true);
                try {
                  const data = await AttributeService.getAttributes(selectedIndustry || undefined);
                  setAttributes((data || []).filter((v, i, a) => a.findIndex(t => t.id === v.id) === i));
                } catch (_e) { /* best-effort reload */ }
                setIsLoadingItems(false);
              }} />
            )}
            {activeTab === 'screen_fields' && (() => {
              const screens = [...new Set(screenFields.map(f => f.ScreenName))].sort();
              const sfSt = sfSearchTerm.toLowerCase();
              const filteredScreens = sfSt ? screens.filter(s => s.toLowerCase().includes(sfSt)) : screens;
              const fieldsForScreen = sfSelectedScreen ? screenFields.filter(f => f.ScreenName === sfSelectedScreen) : [];
              const SF_TYPES = ['text','number','decimal','date','dropdown','checkbox','textarea','email','phone','file-upload','image-url','boolean','lookup','barcode'];
              return (
                <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
                  {/* Left — screen list */}
                  <div style={{ width:240, flexShrink:0, borderRight:`1px solid ${borderColor}`, background:'white', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                    <div style={{ padding:'0.6rem 0.75rem', borderBottom:`1px solid #f1f5f9` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                        <span style={{ fontSize:'0.65rem', fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                          {sfLoading ? 'Loading...' : `${screens.length} Screen${screens.length !== 1 ? 's' : ''}`}
                        </span>
                        <button onClick={() => { setSfShowForm(true); setSfField(BLANK_SF); setSfSelectedScreen(null); }}
                          style={{ padding:'0.2rem 0.55rem', border:`1px solid ${accent}`, borderRadius:'5px', background:'#eff6ff', cursor:'pointer', fontSize:'0.7rem', fontWeight:700, color:accent }}>
                          + Add Field
                        </button>
                      </div>
                      <input style={{ ...inp, fontSize:'0.78rem' }} placeholder="Search screens..." value={sfSearchTerm} onChange={e => setSfSearchTerm(e.target.value)} />
                    </div>
                    <div style={{ flex:1, overflowY:'auto' }}>
                      {sfLoading
                        ? [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
                        : filteredScreens.length === 0
                          ? <div style={{ padding:'2rem', textAlign:'center', color:textMuted, fontSize:'0.8rem' }}>{screenFields.length === 0 ? 'No fields configured yet' : 'No matching screens'}</div>
                          : filteredScreens.map(sc => (
                            <button key={sc} onClick={() => { setSfSelectedScreen(sc); setSfShowForm(false); }}
                              style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.5rem 0.75rem', border:'none', borderBottom:'1px solid #f8fafc', cursor:'pointer', textAlign:'left', background: sfSelectedScreen === sc ? '#eff6ff' : 'transparent', transition:'background 0.1s' }}
                              onMouseEnter={e => { if (!e.currentTarget) return; if (sfSelectedScreen !== sc) e.currentTarget.style.background='#f8fafc'; }}
                              onMouseLeave={e => { if (!e.currentTarget) return; if (sfSelectedScreen !== sc) e.currentTarget.style.background='transparent'; }}>
                              <span style={{ fontSize:'0.8rem', fontWeight: sfSelectedScreen === sc ? 700 : 500, color: sfSelectedScreen === sc ? accent : textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sc}</span>
                              <span style={{ fontSize:'0.68rem', background: sfSelectedScreen === sc ? accent : '#e2e8f0', color: sfSelectedScreen === sc ? 'white' : textMuted, padding:'0.1rem 0.4rem', borderRadius:'10px', flexShrink:0, marginLeft:'4px' }}>
                                {screenFields.filter(f => f.ScreenName === sc).length}
                              </span>
                            </button>
                          ))
                      }
                    </div>
                  </div>
                  {/* Right — field list / form */}
                  <div style={{ flex:1, overflow:'auto', padding:'1.25rem' }}>
                    {sfShowForm ? (
                      <div style={{ maxWidth:560 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
                          <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:textPrimary, margin:0 }}>Add Dynamic Field</h3>
                          <button onClick={() => setSfShowForm(false)} style={{ border:'none', background:'none', cursor:'pointer', color:textMuted, fontSize:'1.2rem', lineHeight:1 }}>×</button>
                        </div>
                        <form onSubmit={handleSaveScreenField}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.75rem' }}>
                            <FF label="Screen Name *"><input style={inp} required value={sfField.ScreenName} onChange={e => setSfField(p => ({ ...p, ScreenName: e.target.value }))} placeholder="e.g. SupplierMaster" /></FF>
                            <FF label="Field Key *"><input style={inp} required value={sfField.FieldKey} onChange={e => setSfField(p => ({ ...p, FieldKey: e.target.value }))} placeholder="e.g. gstNumber" /></FF>
                            <FF label="Field Label *"><input style={inp} required value={sfField.FieldLabel} onChange={e => setSfField(p => ({ ...p, FieldLabel: e.target.value }))} placeholder="Display label" /></FF>
                            <FF label="Field Type">
                              <select style={inp} value={sfField.FieldType} onChange={e => setSfField(p => ({ ...p, FieldType: e.target.value }))}>
                                {SF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </FF>
                            <FF label="Section Name"><input style={inp} value={sfField.SectionName} onChange={e => setSfField(p => ({ ...p, SectionName: e.target.value }))} placeholder="Additional Fields" /></FF>
                            <FF label="Display Order"><input style={inp} type="number" value={sfField.DisplayOrder} onChange={e => setSfField(p => ({ ...p, DisplayOrder: parseInt(e.target.value) || 0 }))} /></FF>
                            <FF label="Placeholder Text" full><input style={inp} value={sfField.PlaceholderText} onChange={e => setSfField(p => ({ ...p, PlaceholderText: e.target.value }))} /></FF>
                            {(sfField.FieldType === 'dropdown' || sfField.FieldType === 'multi-select') && (
                              <FF label="Options (comma-separated)" full><input style={inp} value={sfField.DropdownOptions} onChange={e => setSfField(p => ({ ...p, DropdownOptions: e.target.value }))} placeholder="Option A,Option B,Option C" /></FF>
                            )}
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1rem' }}>
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', fontWeight:600, cursor:'pointer' }}>
                              <input type="checkbox" checked={sfField.IsRequired} onChange={e => setSfField(p => ({ ...p, IsRequired: e.target.checked }))} />
                              Required Field
                            </label>
                          </div>
                          <div style={{ display:'flex', gap:'0.65rem' }}>
                            <button type="submit" disabled={sfSaving} style={{ padding:'0.55rem 1.2rem', background: accent, color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontWeight:700, fontSize:'0.85rem', opacity: sfSaving ? 0.6 : 1 }}>
                              {sfSaving ? 'Saving...' : 'Save Field'}
                            </button>
                            <button type="button" onClick={() => setSfShowForm(false)} style={{ padding:'0.55rem 1rem', background:'#f1f5f9', color:textMuted, border:`1px solid ${borderColor}`, borderRadius:'7px', cursor:'pointer', fontWeight:600, fontSize:'0.85rem' }}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : sfSelectedScreen ? (
                      <div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
                          <div>
                            <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Screen Fields</div>
                            <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>{sfSelectedScreen}</h2>
                          </div>
                          <button onClick={() => { setSfShowForm(true); setSfField({ ...BLANK_SF, ScreenName: sfSelectedScreen }); }}
                            style={{ padding:'0.45rem 0.9rem', background: accent, color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontWeight:700, fontSize:'0.8rem' }}>
                            + Add Field
                          </button>
                        </div>
                        {fieldsForScreen.length === 0 ? (
                          <div style={{ padding:'3rem', textAlign:'center', background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}` }}>
                            <div style={{ fontSize:'2rem', marginBottom:'0.75rem' }}>🔧</div>
                            <p style={{ color:textMuted, fontSize:'0.85rem' }}>No fields configured for this screen yet.</p>
                          </div>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                            {fieldsForScreen.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0)).map(f => (
                              <div key={f.Id} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.65rem 0.9rem', background:'white', borderRadius:'8px', border:`1px solid ${borderColor}`, transition:'border-color 0.15s' }}>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                    <span style={{ fontWeight:700, fontSize:'0.85rem', color:textPrimary }}>{f.FieldLabel}</span>
                                    {f.IsRequired ? <span style={{ fontSize:'0.62rem', background:'#fef2f2', color:'#dc2626', padding:'1px 5px', borderRadius:'4px', fontWeight:700 }}>Required</span> : null}
                                    <span style={{ fontSize:'0.62rem', background:TYPE_COLORS[f.FieldType] ? TYPE_COLORS[f.FieldType]+'18' : '#f1f5f9', color: TYPE_COLORS[f.FieldType] || textMuted, padding:'1px 6px', borderRadius:'4px', fontWeight:700 }}>{f.FieldType}</span>
                                  </div>
                                  <div style={{ fontSize:'0.72rem', color:textMuted, marginTop:'2px' }}>Key: <code style={{ background:'#f1f5f9', padding:'1px 4px', borderRadius:'3px', fontSize:'0.72rem' }}>{f.FieldKey}</code> · Section: {f.SectionName || 'Additional Fields'} · Order: {f.DisplayOrder || 0}</div>
                                </div>
                                <button onClick={() => handleDeleteScreenField(f.Id)} title="Delete field"
                                  style={{ padding:'0.28rem 0.5rem', border:`1px solid #fecaca`, borderRadius:'6px', background:'#fef2f2', cursor:'pointer', color:'#dc2626', fontSize:'0.72rem', fontWeight:700, flexShrink:0 }}>
                                  Del
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding:'5rem 2rem', textAlign:'center', background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}` }}>
                        <div style={{ fontSize:'2.5rem', marginBottom:'1rem' }}>🔧</div>
                        <h3 style={{ fontSize:'1rem', fontWeight:700, color:textPrimary, marginBottom:'0.5rem' }}>Dynamic Screen Fields</h3>
                        <p style={{ color:textMuted, fontSize:'0.82rem', maxWidth:380, margin:'0 auto' }}>
                          Add custom fields to any ERP screen without code changes. Fields are stored in the database and rendered automatically by DynamicFieldRenderer.
                        </p>
                        <button onClick={() => { setSfShowForm(true); setSfField(BLANK_SF); }} style={{ marginTop:'1.25rem', padding:'0.55rem 1.2rem', background:accent, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:700, fontSize:'0.85rem' }}>
                          + Add First Field
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── EMPTY STATE ─────────────────────────────────────────────── */}
            {!['preview','company','bulk_import','screen_fields'].includes(activeTab) && !selectedAttr && !selectedLayout && !selectedSolution && (
              <div style={{ padding:'5rem 2rem', textAlign:'center', background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}` }}>
                <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>{activeTab==='attributes'?'📝':activeTab==='layouts'?'📐':'💡'}</div>
                <h3 style={{ fontSize:'1rem', fontWeight:700, color:textPrimary, marginBottom:'0.5rem' }}>
                  {selectedIndustry ? `Select ${activeTab.slice(0,-1)} to edit` : 'Select an industry first'}
                </h3>
                <p style={{ color:textMuted, fontSize:'0.82rem' }}>
                  {selectedIndustry
                    ? `Browse the ${activeTab} list in the left panel and click any item to start editing.`
                    : 'Click any industry in the left panel to see its attributes, layouts, or solutions.'
                  }
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
